import { useEffect, useState } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useFirebaseSkillsHooks } from './skillsHook';

const Skills = () => {
    const [isVisible, setIsVisible] = useState(false);
    const { language } = useLanguage();
    const skillsData = useFirebaseSkillsHooks(language);
    const {
        title,
        titleHighlight,
        subtitle,
        categories = [],
        softSkills,
    } = skillsData;
    const certificationsTitle = skillsData.certifications?.title;
    const certList = Array.isArray(skillsData.certifications?.list)
        ? skillsData.certifications.list
        : [];
    const softSkillItems = Array.isArray(softSkills?.skills) ? softSkills.skills : [];

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 },
        );

        const element = document.getElementById('skills');
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, []);

    return (
        <section id="skills" className="bg-surface py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div
                    className={`transition-all duration-700 ${
                        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                    }`}
                >
                    <div className="mb-16 max-w-3xl">
                        <h2 className="mb-4 font-display text-4xl font-bold tracking-tight text-fg md:text-5xl">
                            {title ?? 'My'} <span className="text-accent">{titleHighlight ?? 'Skills'}</span>
                        </h2>
                        <p className="text-xl text-muted">{subtitle ?? ''}</p>
                    </div>

                    <div className="mb-16 grid grid-cols-1 gap-8 md:grid-cols-2">
                        {(categories || []).map((category: any, categoryIndex: number) => (
                            <div key={categoryIndex} className="border border-white/10 bg-ink p-8">
                                <div className="mb-6 flex items-center gap-3">
                                    <span className="font-display text-sm font-bold uppercase tracking-widest text-highlight">
                                        {(category.title || '?').slice(0, 1)}
                                    </span>
                                    <h3 className="font-display text-2xl font-bold text-fg">
                                        {category.title}
                                    </h3>
                                </div>

                                <div className="space-y-4">
                                    {category.stats &&
                                        Object.values(category.stats).map((stat: any, statIndex: number) => {
                                            const level = Number(stat.level);
                                            const showBar = Number.isFinite(level) && level > 0;

                                            return (
                                                <div key={statIndex} className="space-y-2">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span className="font-medium text-fg">{stat.name}</span>
                                                        {showBar && (
                                                            <span className="text-sm text-muted">{level}%</span>
                                                        )}
                                                    </div>
                                                    {showBar && (
                                                        <div className="h-1.5 w-full overflow-hidden bg-white/10">
                                                            <div
                                                                className="h-1.5 bg-accent transition-all duration-1000 ease-out"
                                                                style={{
                                                                    width: isVisible ? `${level}%` : '0%',
                                                                    transitionDelay: `${categoryIndex * 120 + statIndex * 80}ms`,
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="border border-white/10 bg-ink p-8">
                        <div className="mb-8 max-w-2xl">
                            <h3 className="mb-2 font-display text-3xl font-bold text-fg">
                                {softSkills?.title}{' '}
                                <span className="text-highlight">{softSkills?.titleHighlight}</span>
                            </h3>
                            <p className="text-muted">{softSkills?.subtitle}</p>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {softSkillItems.map((skill, index) => {
                                const level = Number(skill.level);
                                const showBar = Number.isFinite(level) && level > 0;

                                return (
                                    <div key={index} className="border border-white/10 p-5">
                                        <div className="flex items-baseline justify-between gap-3">
                                            <h4 className="font-display text-lg font-semibold text-fg">
                                                {skill.name}
                                            </h4>
                                            {showBar && (
                                                <span className="text-sm text-accent">{level}%</span>
                                            )}
                                        </div>
                                        {showBar && (
                                            <div className="mt-2 h-1 w-full bg-white/10">
                                                <div
                                                    className="h-1 bg-highlight transition-all duration-1000 ease-out"
                                                    style={{
                                                        width: isVisible ? `${level}%` : '0%',
                                                        transitionDelay: `${index * 100}ms`,
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mt-16">
                        <h3 className="mb-8 font-display text-2xl font-bold text-fg">
                            {certificationsTitle}
                        </h3>
                        <div className="flex flex-wrap gap-3">
                            {[...certList]
                                .sort((a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0))
                                .map((cert: any, index: number) => (
                                    <a
                                        key={index}
                                        href={cert.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="border border-accent/50 px-5 py-2.5 text-sm font-medium text-fg transition-colors hover:border-accent hover:bg-accent/10"
                                    >
                                        {cert.name}
                                    </a>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export { Skills };
